<template>
  <div
    v-if="totalUnreadChecks > 0"
    data-testid="checks-unread-panel"
    class="tw-py-4 tw-px-6 tw-flex tw-flex-col tw-gap-4"
  >
    <InboxNotificationGroup
      v-if="generalUnreadCheckIds.length > 0"
      :name="I18n.t('assistant_check.page.sections.general')"
      :show-mark-as-read="true"
      :disable-mark-as-read="false"
      @mark-as-read="markGeneralChecksAsRead()"
    >
      <sf-assistant-check
        v-for="checkId in generalCheckIds"
        :key="checkId"
        :check="checksById[checkId]"
        @toggle-read="sfAssistantChecksPageStore.toggleCheck(checkId)"
      />
      <template #message-count-indicator>
        {{
          I18n.t('assistant_check.page.checks.count', {
            count: generalUnreadCheckIds.length,
          })
        }}
      </template>
    </InboxNotificationGroup>
    <template v-for="workflowId in workflowIds">
      <InboxNotificationGroup
        v-if="groupedUnreadCheckIdsByWorkflow[workflowId].length > 0"
        :key="workflowId"
        :name="workflowById[workflowId].name"
        :show-mark-as-read="true"
        :disable-mark-as-read="false"
        @mark-as-read="markGroupedChecksAsRead(workflowId)"
      >
        <template v-for="contextId in groupedContextIds[workflowId]">
          <template v-for="checkId in groupedCheckIds[workflowId][contextId]">
            <sf-assistant-check
              v-if="!checksById[checkId].read"
              :key="checkId"
              :check="checksById[checkId]"
              @toggle-read="sfAssistantChecksPageStore.toggleCheck(checkId)"
            />
          </template>
        </template>
        <template #message-count-indicator>
          {{
            I18n.t('assistant_check.page.checks.count', {
              count: groupedUnreadCheckIdsByWorkflow[workflowId].length,
            })
          }}
        </template>
      </InboxNotificationGroup>
    </template>
    <div
      class="
        tw-mx-auto
        tw-my-4
        tw-font-medium
        tw-text-sm
        tw-text-sf-gray-light
        tw-text-center
      "
    >
      {{ I18n.t('assistant_check.page.checks.no_more_unread') }}
      <SfButtonInverted
        small
        inline
        variant="dark"
        :disabled="false"
        @click="markAllAsRead"
      >
        <span
          data-testid="checks-mark-all-as-read"
          class="tw-flex tw-flex-row tw-items-center tw-align-baseline tw-gap-2"
        >
          <i class="icon icon-envelope-open" />
          {{ I18n.t('communication.inbox.mark_all_as_read') }}
        </span>
      </SfButtonInverted>
    </div>
  </div>
  <div
    v-else
    class="
      tw-h-full tw-w-full tw-flex tw-flex-col tw-items-center tw-justify-center
    "
  >
    <span class="tw-font-medium tw-text-2xl">
      {{ I18n.t('assistant_check.page.no_unread') }}
    </span>
  </div>
</template>

<script setup lang="ts">
import useI18n from '@app/shared/vue/composables/use-I18n'
import { storeToRefs } from 'pinia'
import InboxNotificationGroup from '@app/application/communication/screens/inbox/components/inbox-notification-group.vue'
import SfAssistantCheck from '@app/application/sf-assistant-checks/components/sf-assistant-check.vue'
import SfButtonInverted from '@app/shared/vue/components/sf-button/sf-button-inverted.vue'
import { useSfAssistantChecksPage } from '../stores/page'

const I18n = useI18n()

const sfAssistantChecksPageStore = useSfAssistantChecksPage()

const {
  unreadCheckIds,
  totalUnreadChecks,
  generalUnreadCheckIds,
  groupedCheckIds,
  groupedUnreadCheckIdsByWorkflow,
  groupedContextIds,
  workflowById,
  workflowIds,
  checksById,
  generalCheckIds,
} = storeToRefs(sfAssistantChecksPageStore)

const markGeneralChecksAsRead = (): void => {
  tryToMarkAsRead(() =>
    sfAssistantChecksPageStore.markChecksAsRead(generalUnreadCheckIds.value)
  )
}

const markGroupedChecksAsRead = (workflowId: number): void => {
  tryToMarkAsRead(() =>
    sfAssistantChecksPageStore.markChecksAsRead(
      groupedUnreadCheckIdsByWorkflow.value[workflowId]
    )
  )
}

const markAllAsRead = (): void => {
  tryToMarkAsRead(() =>
    sfAssistantChecksPageStore.markChecksAsRead(unreadCheckIds.value)
  )
}
const tryToMarkAsRead = (markAsReadCallback: () => void): void => {
  try {
    markAsReadCallback()
  } catch (ex) {
    sfAssistantChecksPageStore.fetchAll()
  }
  sfAssistantChecksPageStore.fetchUnreadCount()
}
</script>
